import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Tag, Users, Calendar, Edit2, Trash2, Eye,
  X, Megaphone, Hash, Radio
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_OPTIONS = [
  'Referido', 'Recompra', 'Sitio Web', 'Redes Sociales', 'Google Ads',
  'Facebook Ads', 'LinkedIn', 'Evento/Feria', 'Llamada Fría',
  'Email Marketing', 'Alianza Estratégica', 'WhatsApp', 'Instagram',
  'TikTok', 'YouTube', 'Otro'
];

const getChannelColor = (channel) => {
  const colors = {
    'Google Ads': 'bg-blue-100 text-blue-700',
    'Facebook Ads': 'bg-indigo-100 text-indigo-700',
    'Instagram': 'bg-pink-100 text-pink-700',
    'WhatsApp': 'bg-green-100 text-green-700',
    'LinkedIn': 'bg-sky-100 text-sky-700',
    'Email Marketing': 'bg-amber-100 text-amber-700',
    'Redes Sociales': 'bg-purple-100 text-purple-700',
    'Sitio Web': 'bg-teal-100 text-teal-700',
    'TikTok': 'bg-slate-100 text-slate-700',
    'YouTube': 'bg-red-100 text-red-700',
    'Referido': 'bg-orange-100 text-orange-700',
  };
  return colors[channel] || 'bg-slate-100 text-slate-600';
};

export default function MisCampanas() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({ name: '', channel: '', tag: '' });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/campaigns/mis-campanas`, { headers });
      if (!res.ok) throw new Error();
      setCampaigns(await res.json());
    } catch {
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('El nombre es requerido');
    if (!form.tag.trim()) return toast.error('El tag es requerido');
    if (!form.channel) return toast.error('El canal es requerido');

    setSaving(true);
    try {
      const url = editingCampaign
        ? `${API_URL}/api/campaigns/mis-campanas/${editingCampaign.id}`
        : `${API_URL}/api/campaigns/mis-campanas`;
      const method = editingCampaign ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error');
      }

      toast.success(editingCampaign ? 'Campaña actualizada' : 'Campaña creada');
      setShowModal(false);
      setEditingCampaign(null);
      setForm({ name: '', channel: '', tag: '' });
      loadCampaigns();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`¿Eliminar la campaña "${campaign.name}"? Los leads asociados perderán la asignación de campaña.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/campaigns/mis-campanas/${campaign.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      toast.success('Campaña eliminada');
      loadCampaigns();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setForm({ name: campaign.name, channel: campaign.channel, tag: campaign.tag });
    setShowModal(true);
  };

  const openCreate = () => {
    setEditingCampaign(null);
    setForm({ name: '', channel: '', tag: '' });
    setShowModal(true);
  };

  const filteredCampaigns = campaigns.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.tag.toLowerCase().includes(search.toLowerCase()) ||
    c.channel.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div className="p-6 space-y-6" data-testid="mis-campanas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mis Campañas</h1>
          <p className="text-sm text-slate-500 mt-1">Gestiona tus campañas y mide la conversión de cada una</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-campaign-btn">
          <Plus className="w-4 h-4" /> Nueva Campaña
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg"><Megaphone className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Total Campañas</p>
              <p className="text-2xl font-bold text-slate-900">{campaigns.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><Users className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Leads en Campañas</p>
              <p className="text-2xl font-bold text-green-600">{campaigns.reduce((s, c) => s + (c.leadCount || 0), 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg"><Radio className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Canales Activos</p>
              <p className="text-2xl font-bold text-amber-600">{new Set(campaigns.map(c => c.channel)).size}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por nombre, tag o canal..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-campaigns"
        />
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-lg font-medium">
              {search ? 'No se encontraron campañas' : 'No hay campañas creadas'}
            </p>
            {!search && <p className="text-slate-400 text-sm mt-1">Crea tu primera campaña para empezar a medir conversiones</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Campaña</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Tag</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Canal</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Creador</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Leads</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCampaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/campaigns/mis-campanas/${campaign.id}`)}
                    data-testid={`campaign-row-${campaign.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{campaign.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-mono">
                        <Hash className="w-3 h-3" /> {campaign.tag}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getChannelColor(campaign.channel)}`}>
                        {campaign.channel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{campaign.createdByName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatDate(campaign.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {campaign.leadCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => navigate(`/campaigns/mis-campanas/${campaign.id}`)} className="p-2 hover:bg-blue-50 rounded-lg text-slate-400 hover:text-blue-600" title="Ver detalle">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEdit(campaign)} className="p-2 hover:bg-indigo-50 rounded-lg text-slate-400 hover:text-indigo-600" title="Editar">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(campaign)} className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de la Campaña *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Black Friday 2026"
                  data-testid="campaign-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Canal *</label>
                <select
                  value={form.channel}
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  data-testid="campaign-channel-select"
                >
                  <option value="">Seleccionar canal...</option>
                  {CHANNEL_OPTIONS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag identificador *</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={form.tag}
                    onChange={(e) => setForm({ ...form, tag: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                    placeholder="ej: black-friday-2026"
                    className="pl-9 font-mono"
                    data-testid="campaign-tag-input"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Este tag se usará para identificar la campaña al crear leads</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 gap-2" data-testid="save-campaign-btn">
                  {saving ? 'Guardando...' : editingCampaign ? 'Guardar Cambios' : 'Crear Campaña'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
